import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate, formatMoney } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { useAuth } from '@/auth/auth-context';
import { billingApi } from '@/services/billing.api';
import { registrationApi } from '@/services/registration.api';
import { ApiError } from '@/services/api-client';
import { ModulePicker, totalMonthlyCents } from '@/components/module-picker';
import { Alert, Button, Spinner } from '@/components/ui';

/** Billing: the active module bundle, and a picker to change it — the same catalog the registration wizard offers. */
export function BillingPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [managing, setManaging] = useState(false);
  const [draftModuleKeys, setDraftModuleKeys] = useState<string[]>([]);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const subscription = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => billingApi.getSubscription(),
  });
  const catalog = useQuery({
    queryKey: ['billing', 'modules'],
    queryFn: () => registrationApi.listModuleCatalog(),
    enabled: managing,
  });

  const checkout = useMutation({
    mutationFn: (moduleKeys: readonly string[]) =>
      billingApi.checkout({ moduleKeys: [...moduleKeys] }),
    onSuccess: async (response) => {
      setCheckoutError(null);
      if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
        return;
      }
      setManaging(false);
      await queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
    onError: (error: unknown) => {
      setCheckoutError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  function startManaging(): void {
    setDraftModuleKeys([...(subscription.data?.moduleKeys ?? [])]);
    setManaging(true);
  }

  const moduleKeys = subscription.data?.moduleKeys ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">{t('billing.subscription.title')}</h1>
      </header>

      {subscription.isPending && <Spinner label={t('common.states.loading')} />}

      {subscription.isError && (
        <Alert tone="danger">
          {subscription.error instanceof ApiError
            ? t(`errors.${subscription.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {subscription.data && (
        <div className="detail-grid">
          <div className="detail-row">
            <span className="detail-label">{t('billing.subscription.activeModules')}</span>
            <span className="detail-value">
              {moduleKeys.length > 0
                ? moduleKeys.join(', ')
                : t('billing.subscription.baselineOnly')}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('billing.subscription.status')}</span>
            <span className="detail-value">{t(`billing.status.${subscription.data.status}`)}</span>
          </div>
          {subscription.data.trialEndsAt && (
            <div className="detail-row">
              <span className="detail-label">{t('billing.subscription.trialEndsAt')}</span>
              <span className="detail-value" dir="ltr">
                {formatDate(subscription.data.trialEndsAt, locale)}
              </span>
            </div>
          )}
        </div>
      )}

      {checkoutError && <Alert tone="danger">{checkoutError}</Alert>}

      {can('billing.subscription.update') && !managing && (
        <Button variant="primary" onClick={startManaging}>
          {t('billing.subscription.manageModules')}
        </Button>
      )}

      {managing && (
        <>
          {catalog.isPending && <Spinner label={t('common.states.loading')} />}
          {catalog.data && (
            <>
              <ModulePicker
                catalog={catalog.data}
                selectedModuleKeys={draftModuleKeys}
                onChange={setDraftModuleKeys}
              />
              <div className="plan-footer">
                <span className="plan-footer-total">
                  {t('registration.modules.total', {
                    values: {
                      amount: formatMoney(
                        totalMonthlyCents(catalog.data, draftModuleKeys),
                        'USD',
                        locale,
                      ),
                    },
                  })}
                </span>
                <Button
                  variant="primary"
                  loading={checkout.isPending}
                  onClick={() => checkout.mutate(draftModuleKeys)}
                >
                  {t('common.actions.save')}
                </Button>
              </div>
            </>
          )}
          <Button variant="ghost" onClick={() => setManaging(false)}>
            {t('common.actions.cancel')}
          </Button>
        </>
      )}
    </div>
  );
}
